import { useState, useEffect } from 'react';
import { api } from '../lib/api';

const MODELS = [
  { value: 'openai/gpt-5.4', label: 'GPT-5.4 (flagship)' },
  { value: 'openai/gpt-5.4-mini', label: 'GPT-5.4 Mini (budget)' },
  { value: 'openai/gpt-5.4-nano', label: 'GPT-5.4 Nano (cheapest)' },
  { value: 'openai/gpt-5.2', label: 'GPT-5.2 (legacy flagship)' },
  { value: 'anthropic/claude-3-5-haiku', label: 'Claude 3.5 Haiku (fast + cheap)' },
  { value: 'anthropic/claude-3-5-sonnet', label: 'Claude 3.5 Sonnet (balanced)' },
  { value: 'anthropic/claude-opus-4', label: 'Claude Opus 4 (most capable — expensive)' },
];

export default function Settings() {
  const [copied, setCopied] = useState('');
  const [config, setConfig] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');

  // Editable field drafts
  const [openaiKey, setOpenaiKey] = useState('');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [activeModel, setActiveModel] = useState('');
  const [newUserGrant, setNewUserGrant] = useState('');
  const [minTokens, setMinTokens] = useState('');
  const [maxOutputTokens, setMaxOutputTokens] = useState('');
  const [restarting, setRestarting] = useState(false);

  useEffect(() => { loadConfig(); }, []);

  async function loadConfig() {
    setLoading(true);
    try {
      const data = await api.getConfig();
      setConfig(data.config);
      setActiveModel(data.config.ACTIVE_AI_MODEL ?? '');
      setNewUserGrant(data.config.NEW_USER_TOKEN_GRANT ?? '');
      setMinTokens(data.config.MIN_TOKENS_THRESHOLD ?? '');
      setMaxOutputTokens(data.config.AI_MAX_OUTPUT_TOKENS ?? '');
      // Keys show masked values — don't pre-fill input
      setOpenaiKey('');
      setAnthropicKey('');
    } catch (e) {
      showMessage(String(e), 'error');
    } finally {
      setLoading(false);
    }
  }

  function showMessage(msg: string, type: 'success' | 'error' = 'success') {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 4000);
  }

  async function handleSave(updates: Record<string, string>, label: string) {
    // Filter out empty values — only send fields that were actually changed
    const filtered = Object.fromEntries(
      Object.entries(updates).filter(([_, v]) => v !== '')
    );
    if (Object.keys(filtered).length === 0) {
      showMessage('No changes to save', 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await api.updateConfig(filtered);
      setConfig(res.config);
      showMessage(`${label} updated (${res.keys.join(', ')})`);
      // Reset key inputs after save
      if (filtered.OPENAI_API_KEY) setOpenaiKey('');
      if (filtered.ANTHROPIC_API_KEY) setAnthropicKey('');
    } catch (e) {
      showMessage(String(e), 'error');
    } finally {
      setSaving(false);
    }
  }

  function copy(text: string, key: string) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    });
  }

  if (loading) return <p className="text-gray-500">Loading configuration...</p>;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Settings</h1>

      {message && (
        <div className={`mb-4 p-3 rounded text-sm ${messageType === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {message}
        </div>
      )}

      {/* ── Live Config: AI Keys ─────────────────────────────────────────── */}
      <div className="bg-white rounded-lg border p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold">API Keys</h2>
            <p className="text-xs text-gray-500 mt-0.5">Changes take effect immediately — no restart needed</p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">OpenAI API Key</label>
            <div className="text-xs text-gray-400 mb-1">Current: <code className="bg-gray-100 px-1 rounded">{config.OPENAI_API_KEY || '(not set)'}</code></div>
            <div className="flex gap-2">
              <input
                type="password"
                className="flex-1 border rounded px-3 py-2 text-sm font-mono"
                placeholder="sk-proj-... (paste new key)"
                value={openaiKey}
                onChange={(e) => setOpenaiKey(e.target.value)}
              />
              <button
                disabled={saving || !openaiKey}
                onClick={() => handleSave({ OPENAI_API_KEY: openaiKey }, 'OpenAI key')}
                className="px-4 py-2 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Anthropic API Key</label>
            <div className="text-xs text-gray-400 mb-1">Current: <code className="bg-gray-100 px-1 rounded">{config.ANTHROPIC_API_KEY || '(not set)'}</code></div>
            <div className="flex gap-2">
              <input
                type="password"
                className="flex-1 border rounded px-3 py-2 text-sm font-mono"
                placeholder="sk-ant-... (paste new key)"
                value={anthropicKey}
                onChange={(e) => setAnthropicKey(e.target.value)}
              />
              <button
                disabled={saving || !anthropicKey}
                onClick={() => handleSave({ ANTHROPIC_API_KEY: anthropicKey }, 'Anthropic key')}
                className="px-4 py-2 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Live Config: Active Model ────────────────────────────────────── */}
      <div className="bg-white rounded-lg border p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold">Active AI Model</h2>
            <p className="text-xs text-gray-500 mt-0.5">All new AI requests will use the selected model</p>
          </div>
        </div>
        <div className="flex gap-2">
          <select
            className="flex-1 border rounded px-3 py-2 text-sm"
            value={activeModel}
            onChange={(e) => setActiveModel(e.target.value)}
          >
            {MODELS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <button
            disabled={saving || activeModel === config.ACTIVE_AI_MODEL}
            onClick={() => handleSave({ ACTIVE_AI_MODEL: activeModel }, 'AI model')}
            className="px-4 py-2 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* ── Live Config: Token Thresholds ─────────────────────────────────── */}
      <div className="bg-white rounded-lg border p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold">Token Economy</h2>
            <p className="text-xs text-gray-500 mt-0.5">Adjustments apply to new requests/registrations immediately</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New user grant</label>
            <input
              type="number"
              className="w-full border rounded px-3 py-2 text-sm"
              value={newUserGrant}
              onChange={(e) => setNewUserGrant(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Min balance threshold</label>
            <input
              type="number"
              className="w-full border rounded px-3 py-2 text-sm"
              value={minTokens}
              onChange={(e) => setMinTokens(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max output tokens</label>
            <input
              type="number"
              className="w-full border rounded px-3 py-2 text-sm"
              value={maxOutputTokens}
              onChange={(e) => setMaxOutputTokens(e.target.value)}
            />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button
            disabled={saving}
            onClick={() => handleSave({
              NEW_USER_TOKEN_GRANT: newUserGrant,
              MIN_TOKENS_THRESHOLD: minTokens,
              AI_MAX_OUTPUT_TOKENS: maxOutputTokens,
            }, 'Token settings')}
            className="px-4 py-2 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Token Settings'}
          </button>
        </div>
        <div className="mt-4 border-t pt-3">
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-gray-500">Deduction method</dt>
              <dd className="font-medium">Proportional — exact inputTokens + outputTokens</dd>
            </div>
            <div>
              <dt className="text-gray-500">Refund on error</dt>
              <dd className="font-medium">Full deducted amount refunded automatically</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* ── Server Control ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-lg border p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold">Server Control</h2>
            <p className="text-xs text-gray-500 mt-0.5">Restart the API container — config changes are persisted and survive restarts</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button
            disabled={restarting}
            onClick={async () => {
              if (!confirm('Restart the API server? The app will be briefly unavailable.')) return;
              setRestarting(true);
              try {
                await api.restartServer();
                showMessage('Server is restarting... It will be back in a few seconds.');
              } catch {
                showMessage('Restart request sent (connection closed as expected).');
              }
              setTimeout(() => setRestarting(false), 10000);
            }}
            className="px-4 py-2 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 disabled:opacity-50"
          >
            {restarting ? 'Restarting...' : 'Restart API Server'}
          </button>
          <p className="text-xs text-gray-500">Docker will automatically restart the container.</p>
        </div>
      </div>

      {/* ── Reference: Env vars ───────────────────────────────────────────── */}
      <div className="bg-white rounded-lg border p-5 mb-6">
        <h2 className="text-base font-semibold mb-3">Environment variables reference</h2>
        <p className="text-sm text-gray-500 mb-3">These require editing <code className="bg-gray-100 px-1 rounded">backend/.env</code> on the server and a restart.</p>
        {[
          { key: 'DATABASE_URL', desc: 'PostgreSQL connection string' },
          { key: 'JWT_SECRET', desc: '≥32 char random string for signing JWTs' },
          { key: 'EMAIL_VERIFICATION_SECRET', desc: '≥32 char random string for email tokens' },
          { key: 'SMTP_HOST', desc: 'SMTP server (e.g. smtp.gmail.com or internal mailserver)' },
          { key: 'SMTP_PORT', desc: '587 for Gmail STARTTLS · 25 for internal relay' },
          { key: 'SMTP_SECURE', desc: 'true only for TLS port 465, otherwise false' },
          { key: 'SMTP_USER', desc: 'Gmail address for auth, or blank for internal relay' },
          { key: 'SMTP_PASS', desc: '16-char Gmail App Password, or blank for internal relay' },
          { key: 'FEEDBACK_NOTIFY_EMAIL', desc: 'Admin email for feedback alerts (defaults to ADMIN_BOOTSTRAP_EMAIL)' },
          { key: 'GOOGLE_CLIENT_ID', desc: 'Google OAuth2 client ID from Cloud Console' },
          { key: 'GOOGLE_CLIENT_SECRET', desc: 'Google OAuth2 client secret' },
          { key: 'GOOGLE_REDIRECT_URI', desc: 'https://api.ospi.link/auth/google/callback' },
          { key: 'APPLE_APP_BUNDLE_ID', desc: 'com.yourcompany.adhdapp (from Apple Developer)' },
          { key: 'ADMIN_BOOTSTRAP_EMAIL', desc: 'Email that gets auto-promoted to admin on first login' },
          { key: 'BUILDS_STORAGE_PATH', desc: 'Absolute path on server for APK/IPA files' },
          { key: 'BUILDS_BASE_URL', desc: 'Public URL prefix for build downloads' },
          { key: 'AI_REQUEST_RETENTION_DAYS', desc: 'Days to keep AI request logs (default 90)' },
          { key: 'PORT', desc: 'Server port (default 3000)' },
        ].map(({ key, desc }) => (
          <div key={key} className="flex items-start justify-between border-b py-2 last:border-0">
            <div>
              <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-mono">{key}</code>
              <p className="text-xs text-gray-500 mt-0.5 ml-1">{desc}</p>
            </div>
            <button onClick={() => copy(key, key)} className="text-xs text-indigo-600 hover:text-indigo-800 ml-2 shrink-0">
              {copied === key ? '✓ Copied' : 'Copy'}
            </button>
          </div>
        ))}
      </div>

      {/* ── Reference: Server commands ────────────────────────────────────── */}
      <div className="bg-white rounded-lg border p-5">
        <h2 className="text-base font-semibold mb-3">Useful server commands</h2>
        {[
          { label: 'View API logs (tail)', cmd: 'docker-compose logs -f api' },
          { label: 'Run DB migrations', cmd: 'docker-compose exec api node dist/db/migrate.js' },
          { label: 'Restart API container', cmd: 'docker-compose restart api' },
          { label: 'Deploy latest (manual)', cmd: 'bash /opt/adhd-app/scripts/deploy.sh' },
        ].map(({ label, cmd }) => (
          <div key={cmd} className="border-b py-2 last:border-0">
            <p className="text-xs text-gray-500 mb-1">{label}</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-gray-900 text-green-400 px-3 py-2 rounded text-xs font-mono">{cmd}</code>
              <button onClick={() => copy(cmd, cmd)} className="text-xs px-2 py-1.5 bg-gray-100 rounded hover:bg-gray-200 shrink-0">
                {copied === cmd ? '✓' : 'Copy'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
